import axios from 'axios';
import { sharedRef, useVSFContext, Logger } from "@vue-storefront/core";
import { initialize as initializeSdk, TYPE_CONTAINER_ITEM_UNDEFINED } from '@bloomreach/spa-sdk';
import { watch, reactive, useRoute, useContext, computed } from '@nuxtjs/composition-api'
// @ts-ignore
import BRXComponent from "~/components/cms/page/BRXComponent.vue";
// @ts-ignore
import Menu from "~/components/cms/layout/Menu.vue";
// @ts-ignore
import Header from "~/components/cms/layout/Header.vue";
// @ts-ignore
import SimplifiedHeader from "~/components/cms/layout/SimplifiedHeader.vue";
// @ts-ignore
import StyleGuide from "~/components/cms/layout/StyleGuide.vue";
// @ts-ignore
import Meta from '~/components/cms/layout/Meta.vue';
// @ts-ignore
import Footer from '~/components/cms/layout/Footer.vue';
import { megaMenuParser } from './initMegaMenu';
import { resolveAllJsonRefs } from "~/composables/helpers/internals/";
import { bloomreachGetters } from "~/composables/getters";
import { useGlobalLabelsStore } from "~/stores/global-labels-store";

let bloomreachCallsIndex = 0;
let BRCacehBustingLimit = 30;

const useBloomreach = (id: string = '') => {
  const route = useRoute();
  const context = useVSFContext();
  const globalLabelsStore = useGlobalLabelsStore();
  const { error: nuxtError, i18n } = useContext();
  const cacheBustingParam = Date.now();

  const BR_ENVIRONMENT = context.$config.brxENV || "dev-fiskars";
  const brxChannelId =
    i18n.localeProperties.channelId || "moominarabia-english-1";
  const appKey =
    i18n.localeProperties.appKey || "moominarabiafi";
  const localeCode = i18n.localeProperties.code;
  const content = sharedRef<any>(null, `content-${id}-${appKey}`);
  const megaMenuContent = sharedRef<any>(
    null,
    `megaMenuContent-${id}-${appKey}`
  );
  const globalLabels = sharedRef<any>(null, `globalLabels-${id}-${appKey}`);
  const userLoginLabels = sharedRef<any>(
    null,
    `userLoginLabels-${id}-${appKey}`
  );
  const userRegisterLabels = sharedRef<any>(
    null,
    `userRegisterLabels-${id}-${appKey}`
  );
  const productData = sharedRef<any>(null, `productData-${id}-${appKey}`);
  const configuration = reactive({
    endpoint:
      route.value.query.endpoint ||
      `https://${BR_ENVIRONMENT}.bloomreach.io/delivery/site/v1/channels/${brxChannelId}/pages`,
    httpClient: axios,
    path: getPathWithoutLocale(route.value.fullPath, localeCode),
  });

  const initMegaMenu = async (simplified = false) => {
    bloomreachCallsIndex++;
    if (!!megaMenuContent.value && bloomreachCallsIndex <= BRCacehBustingLimit) return;
    bloomreachCallsIndex = 0;

    const MEGA_MENU_ENDPOINT = `https://${BR_ENVIRONMENT}.bloomreach.io/delivery/site/v1/channels/${brxChannelId}/documents/content/documents/${brxChannelId}/site-content/common/mega-menu/megamenu/megamenu?_maxreflevel=5`;
    const MEGA_MENU_SIMPLIFIED_ENDPOINT = `https://${BR_ENVIRONMENT}.bloomreach.io/delivery/site/v1/channels/${brxChannelId}/documents/content/documents/${brxChannelId}/site-content/common/moomin-mega-menu/megamenu/megamenu?_maxreflevel=5`;
    const active_mega_menu = simplified ? MEGA_MENU_SIMPLIFIED_ENDPOINT : MEGA_MENU_ENDPOINT;

    const response = await fetch(active_mega_menu);

    return response
      .json()
      .then((resp) => {
        megaMenuContent.value = megaMenuParser(resp);
      })
      .catch((err) => {
        megaMenuContent.value = [];
      });
  };

  const fetchLabels = async () => {
    bloomreachCallsIndex++;
    if (!!globalLabels.value && bloomreachCallsIndex <= BRCacehBustingLimit) return;
    bloomreachCallsIndex = 0;

    try {
      const response = await fetch(
        `https://${BR_ENVIRONMENT}.bloomreach.io/delivery/site/v1/channels/${brxChannelId}/documents/content/documents/${brxChannelId}/site-settings/settings/global-settings/?_maxreflevel=4`
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch global labels. Status: ${response.status}`
        );
      }

      const resp = await response.json();
      const resolved = resolveAllJsonRefs(resp);
      globalLabels.value = resolved;
      globalLabelsStore.storeLabels(resolved);
      return resolved;
    } catch (err) {
      Logger.error("Error fetching global labels:", err);
      globalLabels.value = []; // Set an appropriate fallback value if needed
      throw err; // Rethrow the error for the caller to handle
    }
  };

  const fetchUserLoginForm = async () => {
    try {
      const response = await fetch(
        `https://${BR_ENVIRONMENT}.bloomreach.io/delivery/site/v1/channels/${brxChannelId}/documents/content/documents/${brxChannelId}/site-content/forms/form_userlogin?_maxreflevel=4`
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch labels. Status: ${response.status}`
        );
      }

      const resp = await response.json();
      const resolved = resolveAllJsonRefs(resp);
      userLoginLabels.value = resolved;

      return resolved;
    } catch (err) {
      Logger.error("Error fetching user login labels:", err);
      userLoginLabels.value = []; // Set an appropriate fallback value if needed
      throw err; // Rethrow the error for the caller to handle
    }
  };

  const fetchUserRegisterForm = async () => {
    try {
      const response = await fetch(
        `https://${BR_ENVIRONMENT}.bloomreach.io/delivery/site/v1/channels/${brxChannelId}/documents/content/documents/${brxChannelId}/site-content/forms/form_userregistration?_maxreflevel=4`
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch  labels. Status: ${response.status}`
        );
      }

      const resp = await response.json();
      const resolved = resolveAllJsonRefs(resp);
      userRegisterLabels.value = resolved;

      return resolved;
    } catch (err) {
      Logger.error("Error fetching user registration labels:", err);
      userRegisterLabels.value = []; // Set an appropriate fallback value if needed
      throw err; // Rethrow the error for the caller to handle
    }
  };


  const fetchProductData = async (params: any) => {
    try {
      const response = await fetch(
          `https://${BR_ENVIRONMENT}.bloomreach.io/delivery/site/v1/channels/${brxChannelId}/documents/content/documents/${brxChannelId}/product-repository/products/${params.id}/?_maxreflevel=4&_cacheBust=${cacheBustingParam}`
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product data. Status: ${response.status}`
        );
      }

      const resp = await response.json();
      productData.value = resp;

      return resp;
    } catch (err) {
      nuxtError({ statusCode: 404 });
    }
  };

  const fetchProductImages = async (params: any) => {
    try {
      const response = await fetch(
          `https://${BR_ENVIRONMENT}.bloomreach.io/delivery/images/v1/content/gallery/channel-templates/vuestorefront/moomin/products/${params.id}/?_maxreflevel=4&_cacheBust=${cacheBustingParam}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          // If the response status is 404, return an empty array
          productData.value = [];
          return;
        }

        throw new Error(
          `Failed to fetch product data. Status: ${response.status}`
        );
      }

      const resp = await response.json();
      const productImages = resp.items || [];

      // Sort the productImages using a custom compare function
      const sortedImages = productImages.sort((a, b) => {
        const regex = /_(\d+)\.(jpg|jpeg|webp|gif|webm|mp4|png)/;
        const matchA = a.fileName.match(regex);
        const matchB = b.fileName.match(regex);

        if (!matchA || !matchB) {
          // Handle cases where the filename format doesn't match
          return 0;
        }

        const numberA = parseInt(matchA[1], 10);
        const numberB = parseInt(matchB[1], 10);

        return numberA - numberB;
      });

      // Map the productImages using getImage
      productData.value = sortedImages.map((imgObj) =>
        bloomreachGetters.getImage(imgObj)
      );
    } catch (err) {
      // Handle errors, e.g., log the error or display an error message
      console.error("Error fetching product images:", err);
    }
  };


  const componentsMapping = {
    moominSimplifiedHeader: SimplifiedHeader,
    moominHeader: Header,
    menu: Menu,
    moominFooter: Footer,
    meta: Meta,
    "style-guide": StyleGuide,
    [TYPE_CONTAINER_ITEM_UNDEFINED]: BRXComponent,
  };

  async function initialize() {
    try {
      content.value = await initializeSdk(configuration);
      await fetchLabels();
      await fetchUserLoginForm();
      await fetchUserRegisterForm();

    } catch (err) {
      const statusCode = err.response?.status;
      nuxtError({ statusCode });
    }
  }

  function extractId(pageSlug) {
    // Remove query string and fragment identifiers
    const cleanUrl = pageSlug.split('?')[0].split('#')[0];

    // Remove trailing slashes and split into parts
    const parts = cleanUrl.replace(/\/+$/, '').split('/');

    // Get the last non-empty part
    const lastPart = parts.pop() || '';

    // Extract the trailing numeric ID
    const match = lastPart.match(/\d+$/);

    return match ? match[0] : "";
  }

  function getPathWithoutLocale(path: string, localeCode: string): string {
    if(path.includes("/p/")){
      path = `/p/${extractId(path)}`;
    }

    const resolved = path.startsWith(`/${localeCode}`)
      ? path.slice(localeCode.length + 1)
      : path;
    if(resolved.includes("?")) {
      return `${resolved}&_maxreflevel=5`;
    }
     return `${resolved}?_maxreflevel=5`;
  }

  watch(
    () => route.value.fullPath,
    async (to) => {
      configuration.path = getPathWithoutLocale(to, localeCode);

      if (!content.value) {
        await initialize();
      }
    }
  );


  return {
    content,
    initialize,
    configuration,
    componentsMapping,
    initMegaMenu,
    megaMenuContent,
    fetchLabels,
    fetchUserLoginForm,
    fetchUserRegisterForm,
    globalLabels: computed(() => globalLabels.value),
    userLoginLabels:computed(() => userLoginLabels.value),
    userRegisterLabels:computed(() => userRegisterLabels.value),
    fetchProductData,
    fetchProductImages,
    productData: computed(() => productData.value),
  };
};

export default useBloomreach;
